import React from 'react';
import styled from 'styled-components';
import { useDragScroll } from '../../../utils/useDragScroll';

export type GridItem = {
  label: React.ReactNode;
  value: React.ReactNode;
  key?: string | number;
};

type GridTableProps = {
  items: GridItem[];
  maxColumns?: number;
  gap?: string;
  labelFontSize?: string;
  valueFontSize?: string;
  layout?: 'row' | 'column';
  className?: string;
};

export default function GridTable({
  items,
  maxColumns = 4,
  layout = 'row',
  className,
}: GridTableProps) {
  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  if (layout === 'row') {
    const chunks: GridItem[][] = [];
    for (let i = 0; i < items.length; i += maxColumns) {
      chunks.push(items.slice(i, i + maxColumns));
    }

    return (
      <Wrapper
        ref={ref}
        onMouseDown={onMouseDown}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        className={className}
      >
        {chunks.map((chunk, idx) => (
          <Row key={idx} cols={maxColumns}>
            {chunk.map((it, i) => (
              <Cell key={it.key ?? i}>
                <CellLabel>{it.label}</CellLabel>
                <CellValue>{it.value}</CellValue>
              </Cell>
            ))}
          </Row>
        ))}
      </Wrapper>
    );
  } else {
    return (
      <WrapperColumn
        ref={ref}
        onMouseDown={onMouseDown}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        className={className}
      >
        {items.map((it, i) => (
          <ColumnRow key={it.key ?? i}>
            <ColumnLabel>{it.label}</ColumnLabel>
            <ColumnValue>{it.value}</ColumnValue>
          </ColumnRow>
        ))}
      </WrapperColumn>
    );
  }
}

const Wrapper = styled.div`
  color: #424854;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
`;

const WrapperColumn = styled.div`
  display: flex;
  flex-direction: row;
  color: #424854;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  width: 100%;
  box-sizing: border-box;
`;

const Row = styled.div<{ cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.cols}, 1fr);
  gap: 0;
  align-items: stretch;
  margin-bottom: 8px;
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
`;

const CellLabel = styled.div`
  background-color: #f4f5f7;
  border-bottom: 1px solid #e8e9ed;
  padding: 12px 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CellValue = styled.div`
  padding: 10px;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ColumnRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 16px;
`;

const ColumnLabel = styled.div`
  width: 120px;
  text-align: left;
  background-color: #f4f5f7;
  border-bottom: 1px solid #e8e9ed;
  padding: 12px 16px;
`;

const ColumnValue = styled.div`
  width: 312px;
  font-size: 14px;
  text-align: left;
  word-break: normal;
  overflow-wrap: break-word;
  padding: 12px 16px;
`;
